import React, { useState, useEffect } from 'react';
import { AiAgentsSection } from '../components/AiAgentsSection';
import type { FormHeaderAction } from '../components/FormHeader';
import { useGoBack } from '../hooks/useGoBack';
import { designTokens } from '../theme/design-system';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  RefreshControl,
  Platform,
  Dimensions,
} from 'react-native';
import { colors, spacing, typography, textStyles } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { createTranslator } from '../i18n';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { apiService, BusinessUser, InviteUserData, UserLimits } from '../services/api';
import { Header, ScreenLayout, FormHeader, BottomTabs, Modal, ConfirmationModal, MoreOptionsModal, PermissionSummaryChip, BusinessUserPicker } from '../components';
import { PermissionSummary } from '../constants/permissionLevels';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { useThemedStyles } from '../hooks/useThemedStyles';

const { width: screenWidth } = Dimensions.get('window');
const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768);

interface UserSetupScreenProps {
  onNavigateToConversation?: (conversationId: string) => void;
}

export const UserSetupScreen: React.FC<UserSetupScreenProps> = ({ onNavigateToConversation }) => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo, setActiveTab, activeTab } = useAppContext();
  const goBack = useGoBack('settings');
  const { user, currentBusiness } = useAppStore();
  const { locale } = useLocale();
  const t = createTranslator(locale);
  const rtl = useRTLStyles();

  // State
  const [users, setUsers] = useState<BusinessUser[]>([]);
  const [userLimits, setUserLimits] = useState<UserLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState<InviteUserData>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'member',
    notes: ''
  });
  const [inviteDepartmentIds, setInviteDepartmentIds] = useState<string[]>([]);
  const [showInviteDepartmentPicker, setShowInviteDepartmentPicker] = useState(false);
  const [allDepartments, setAllDepartments] = useState<Array<{ _id: string; name: string }>>([]);
  const [inviting, setInviting] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<BusinessUser | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<BusinessUser[]>([]);
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [selectedPaneUser, setSelectedPaneUser] = useState<BusinessUser | null>(null);
  const [userDepartments, setUserDepartments] = useState<Record<string, Array<{ _id: string; name: string }>>>({});

  // Bulk selection
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [userSummaries, setUserSummaries] = useState<Record<string, PermissionSummary>>({});
  const [showBulkRoleModal, setShowBulkRoleModal] = useState(false);
  const [bulkRoleSaving, setBulkRoleSaving] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkDepartmentPicker, setShowBulkDepartmentPicker] = useState(false);
  const [bulkDepartmentSaving, setBulkDepartmentSaving] = useState(false);

  // Filter users based on search query
  const filterUsers = (users: BusinessUser[], query: string) => {
    if (!query.trim()) return users;

    const lowercaseQuery = query.toLowerCase();
    return users.filter(user =>
      (user.user?.firstName ?? '').toLowerCase().includes(lowercaseQuery) ||
      (user.user?.lastName ?? '').toLowerCase().includes(lowercaseQuery) ||
      (user.user?.email ?? '').toLowerCase().includes(lowercaseQuery) ||
      user.role.toLowerCase().includes(lowercaseQuery) ||
      (user.user?.phone && user.user.phone.includes(query))
    );
  };

  // Update filtered users when search query or users change
  useEffect(() => {
    const filtered = filterUsers(users, searchQuery);
    setFilteredUsers(filtered);
  }, [users, searchQuery]);

  // Load data
  const loadData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      if (!currentBusiness?._id) {
        console.log('⚠️ [UserSetupScreen] No business ID available, skipping data load');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      console.log('🔍 [UserSetupScreen] Loading data for business:', currentBusiness._id);

      const [usersResult, limitsResult, departmentsResult] = await Promise.all([
        apiService.getBusinessUsers(currentBusiness._id),
        apiService.getUserLimits(currentBusiness._id),
        apiService.getDepartments(currentBusiness._id)
      ]);

      if (departmentsResult.statusCode === 1000 && departmentsResult.data) {
        setAllDepartments(departmentsResult.data);
      }

      console.log('📊 [UserSetupScreen] Users result:', usersResult);
      console.log('📊 [UserSetupScreen] Limits result:', limitsResult);

      if (usersResult.statusCode === 1000) {
        console.log('🔍 [UserSetupScreen] Users data received:', usersResult.data);
        console.log('🔍 [UserSetupScreen] First user userId type:', typeof usersResult.data?.[0]?.userId);
        console.log('🔍 [UserSetupScreen] First user userId value:', usersResult.data?.[0]?.userId);
        console.log('🔍 [UserSetupScreen] First user full data:', JSON.stringify(usersResult.data?.[0], null, 2));
        // AI agents have their own section (and number in the plan); people only here.
        const loadedUsers = (usersResult.data || []).filter(u => !u.isAiAgent && u.role !== 'ai_agent');
        setUsers(loadedUsers);
        const userIds = loadedUsers.map(u => u.userId).filter(Boolean);
        if (currentBusiness?._id && userIds.length > 0) {
          apiService.getBulkUserSummaries(currentBusiness._id, userIds).then(summariesResult => {
            if (summariesResult.statusCode === 1000 && summariesResult.data) {
              setUserSummaries(summariesResult.data);
            }
          }).catch(err => console.error('❌ [UserSetupScreen] Error loading permission summaries:', err));

          Promise.all(
            userIds.map(async (userId) => {
              try {
                const departmentsResult = await apiService.getUserDepartments(userId, currentBusiness._id);
                return [userId, departmentsResult.statusCode === 1000 ? (departmentsResult.data || []) : []] as const;
              } catch (err) {
                console.error('❌ [UserSetupScreen] Error loading user departments:', err);
                return [userId, []] as const;
              }
            })
          ).then(entries => {
            setUserDepartments(Object.fromEntries(entries));
          });
        }
      } else {
        showNotification('error', t('userSetup.failedToLoadUsersTitle'), usersResult.errors?.[0]?.message || t('userSetup.unknownError'));
      }

      if (limitsResult.statusCode === 1000) {        setUserLimits(limitsResult.data || null);
      } else {
        console.log('❌ [UserSetupScreen] Limits error:', limitsResult.errors);
        showNotification('error', t('userSetup.failedToLoadLimitsTitle'), limitsResult.errors?.[0]?.message || t('userSetup.unknownError'));
      }
    } catch (error: any) {
      console.error('Error loading user data:', error);
      showNotification('error', t('userSetup.errorTitle'), t('userSetup.loadUserDataError'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Load data when business is available
  useEffect(() => {
    if (currentBusiness?._id) {
      console.log('✅ [UserSetupScreen] Business ID available, loading data...');
      loadData();
    } else {
      console.log('⚠️ [UserSetupScreen] No business ID available yet');
    }
  }, [currentBusiness?._id]);

  // Handle refresh
  const onRefresh = () => {
    loadData(true);
  };

  // Handle tab press (same as HomeScreen)
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab, { onMore: () => setShowMoreModal(true) });

  // Handle invite user
  const handleInviteUser = async () => {
    if (!currentBusiness?._id) {
      showNotification('error', t('userSetup.noBusinessError'), t('userSetup.noBusinessErrorDesc'));
      return;
    }

    // Enhanced validation with specific field checks
    if (!inviteData.email?.trim()) {
      showNotification('error', t('userSetup.emailRequiredTitle'), t('userSetup.emailRequiredDesc'));
      return;
    }

    if (!inviteData.firstName?.trim()) {
      showNotification('error', t('userSetup.firstNameRequiredTitle'), t('userSetup.firstNameRequiredDesc'));
      return;
    }

    if (!inviteData.lastName?.trim()) {
      showNotification('error', t('userSetup.lastNameRequiredTitle'), t('userSetup.lastNameRequiredDesc'));
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteData.email.trim())) {
      showNotification('error', t('userSetup.invalidEmailTitle'), t('userSetup.invalidEmailDesc'));
      return;
    }

    try {
      setInviting(true);
      const payload: InviteUserData = { ...inviteData, departmentIds: inviteDepartmentIds };
      console.log('🔍 [UserSetupScreen] Inviting user with data:', payload);
      const result = await apiService.inviteUser(currentBusiness._id, payload);
      console.log('🔍 [UserSetupScreen] Invitation result:', result);

      if (result.statusCode === 1000) {
        showNotification('success', t('userSetup.invitationSentTitle'), t('userSetup.invitationSentDesc', { email: inviteData.email }));
        setShowInviteModal(false);
        setInviteData({
          email: '',
          firstName: '',
          lastName: '',
          role: 'member',
          notes: ''
        });
        setInviteDepartmentIds([]);
        loadData(); // Refresh the list
      } else {
        showNotification('error', t('userSetup.invitationFailedTitle'), result.errors?.[0]?.message || t('userSetup.invitationFailedDefault'));
      }
    } catch (error: any) {
      console.error('Error inviting user:', error);
      const errorMessage = error?.message || error?.errors?.[0]?.message || t('userSetup.invitationFailedDefault');
      showNotification('error', t('userSetup.errorTitle'), errorMessage);
    } finally {
      setInviting(false);
    }
  };


  // Handle remove user
  const handleRemoveUser = (user: BusinessUser) => {
    console.log('🔍 [UserSetupScreen] handleRemoveUser called with user:', user);
    console.log('🔍 [UserSetupScreen] user.userId:', user.userId);
    console.log('🔍 [UserSetupScreen] typeof user.userId:', typeof user.userId);

    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Handle confirm delete user
  const handleConfirmDeleteUser = async () => {
    if (!currentBusiness?._id || !selectedUser) return;

    try {
      console.log('🔍 [UserSetupScreen] Calling removeUser with:', {
        businessId: currentBusiness._id,
        userId: selectedUser.userId,
        userIdType: typeof selectedUser.userId
      });
      const result = await apiService.removeUser(currentBusiness._id, selectedUser.userId);

      if (result.statusCode === 1000) {
        showNotification('success', t('userSetup.userRemovedTitle'), t('userSetup.userRemovedDesc'));
        loadData(); // Refresh the list
      } else {
        showNotification('error', t('userSetup.removeFailedTitle'), result.errors?.[0]?.message || t('userSetup.removeFailedDefault'));
      }
    } catch (error: any) {
      console.error('Error removing user:', error);
      showNotification('error', t('userSetup.errorTitle'), t('userSetup.removeUserErrorDefault'));
    } finally {
      setShowDeleteModal(false);
      setSelectedUser(null);
    }
  };

  // Handle resend invitation
  const handleResendInvitation = (user: BusinessUser) => {
    setSelectedUser(user);
    setShowResendModal(true);
  };

  // Handle confirm resend invitation
  const handleConfirmResendInvitation = async () => {
    if (!currentBusiness?._id || !selectedUser) return;

    try {
      const result = await apiService.resendInvitation(currentBusiness._id, selectedUser.userId);

      if (result.statusCode === 1000) {
        showNotification('success', t('userSetup.invitationResentTitle'), t('userSetup.invitationResentDesc'));
        loadData(); // Refresh the list
      } else {
        showNotification('error', t('userSetup.resendFailedTitle'), result.errors?.[0]?.message || t('userSetup.resendFailedDefault'));
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      showNotification('error', t('userSetup.errorTitle'), t('userSetup.resendErrorDefault'));
    } finally {
      setShowResendModal(false);
      setSelectedUser(null);
    }
  };

  // Get role color
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return colors.primary;
      case 'admin': return colors.warning;
      case 'manager': return colors.info;
      case 'member': return colors.success;
      case 'viewer': return colors.textLight;
      default: return colors.textLight;
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return colors.success;
      case 'pending': return colors.warning;
      case 'suspended': return colors.error;
      case 'left': return colors.textLight;
      default: return colors.textLight;
    }
  };

  // Get localized role label
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return t('userSetup.roleAdmin');
      case 'manager': return t('userSetup.roleManager');
      case 'member': return t('userSetup.roleMember');
      case 'viewer': return t('userSetup.roleViewer');
      case 'ai_agent': return t('aiAgents.badge');
      default: return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  // Get localized status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return t('userSetup.statusActive');
      case 'pending': return t('userSetup.statusPending');
      case 'suspended': return t('userSetup.statusSuspended');
      case 'left': return t('userSetup.statusLeft');
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Handle role change
  const handleRoleChange = (user: BusinessUser) => {
    console.log('🎨 [UserSetupScreen] handleRoleChange called with user:', user);
    console.log('🎨 [UserSetupScreen] User role:', user.role);
    setSelectedUser(user);
    setShowRoleModal(true);
  };

  // Handle role update
  const handleUpdateUserRole = async (userId: string, newRole: string) => {
    console.log('🎨 [UserSetupScreen] handleUpdateUserRole called with:', { userId, newRole, businessId: currentBusiness?._id });
    
    if (!currentBusiness?._id) return;

    try {
      const result = await apiService.updateUser(currentBusiness._id, userId, { role: newRole as any });
      console.log('🎨 [UserSetupScreen] Update result:', result);

      if (result.statusCode === 1000) {
        showNotification('success', t('userSetup.roleUpdatedTitle'), t('userSetup.roleUpdatedDesc'));
        loadData(); // Refresh the list
      } else {
        showNotification('error', t('userSetup.updateFailedTitle'), result.errors?.[0]?.message || t('userSetup.updateFailedDefault'));
      }
    } catch (error: any) {
      console.error('Error updating user role:', error);
      showNotification('error', t('userSetup.errorTitle'), t('userSetup.updateRoleErrorDefault'));
    } finally {
      setShowRoleModal(false);
      setSelectedUser(null);
    }
  };

  const toggleUserSelect = (userId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const selectableUserIds = filteredUsers.filter(u => u.role !== 'owner').map(u => u.userId);

  const handleSelectAllUsers = () => {
    if (selectedIds.size === selectableUserIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableUserIds));
    }
  };

  const exitUserSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkRoleConfirm = async (newRole: 'admin' | 'manager' | 'member' | 'viewer') => {
    if (!currentBusiness?._id || selectedIds.size === 0) return;
    try {
      setBulkRoleSaving(true);
      const result = await apiService.bulkUpdateUserRole(currentBusiness._id, Array.from(selectedIds), newRole);
      if (result.statusCode === 1000 && result.data) {
        showNotification('success', t('userSetup.roleUpdatedTitle'), t('userSetup.bulk.roleUpdateSuccess', { count: result.data.updated }));
        setShowBulkRoleModal(false);
        exitUserSelectionMode();
        loadData();
      } else {
        showNotification('error', t('userSetup.updateFailedTitle'), result.errors?.[0]?.message || t('userSetup.updateFailedDefault'));
      }
    } catch (error: any) {
      console.error('❌ [UserSetupScreen] Error bulk-updating roles:', error);
      showNotification('error', t('userSetup.errorTitle'), t('userSetup.updateRoleErrorDefault'));
    } finally {
      setBulkRoleSaving(false);
    }
  };

  const handleBulkRemoveConfirm = async () => {
    if (!currentBusiness?._id || selectedIds.size === 0) return;
    try {
      setBulkDeleting(true);
      const result = await apiService.bulkRemoveUsers(currentBusiness._id, Array.from(selectedIds));
      if (result.statusCode === 1000 && result.data) {
        showNotification('success', t('common.success'), t('userSetup.bulk.removeSuccess', { count: result.data.updated }));
        setShowBulkDeleteModal(false);
        exitUserSelectionMode();
        loadData();
      } else {
        showNotification('error', t('userSetup.errorTitle'), result.errors?.[0]?.message || t('userSetup.unknownError'));
      }
    } catch (error: any) {
      console.error('❌ [UserSetupScreen] Error bulk-removing users:', error);
      showNotification('error', t('userSetup.errorTitle'), t('userSetup.unknownError'));
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkDepartmentConfirm = async (departmentIds: string[]) => {
    const departmentId = departmentIds[0];
    if (!departmentId || selectedIds.size === 0) {
      setShowBulkDepartmentPicker(false);
      return;
    }
    try {
      setBulkDepartmentSaving(true);
      const result = await apiService.bulkAddUsersToDepartment(departmentId, Array.from(selectedIds));
      if (result.statusCode === 1000 && result.data) {
        showNotification('success', t('common.success'), t('userSetup.bulk.departmentAssignSuccess', { count: result.data.added }));
        setShowBulkDepartmentPicker(false);
        exitUserSelectionMode();
        loadData();
      } else {
        showNotification('error', t('userSetup.errorTitle'), result.errors?.[0]?.message || t('userSetup.unknownError'));
      }
    } catch (error: any) {
      console.error('❌ [UserSetupScreen] Error bulk-assigning department:', error);
      showNotification('error', t('userSetup.errorTitle'), t('userSetup.unknownError'));
    } finally {
      setBulkDepartmentSaving(false);
    }
  };

  const handleBulkSetPermissions = () => {
    navigateTo('business-permissions', undefined, {
      targetType: 'user',
      targetIds: Array.from(selectedIds),
    });
  };

  const getUserDisplayName = (businessUser: BusinessUser | null) => {
    if (!businessUser) return '';
    return businessUser.user?.firstName || businessUser.user?.lastName
      ? `${businessUser.user?.firstName ?? ''} ${businessUser.user?.lastName ?? ''}`.trim()
      : (businessUser.user?.email ?? t('userSetup.pendingInvite'));
  };

  const handleUserCardPress = (businessUser: BusinessUser) => {
    if (selectionMode && businessUser.role !== 'owner') {
      toggleUserSelect(businessUser.userId);
      return;
    }
    if (Platform.OS === 'web') {
      setSelectedPaneUser(businessUser);
    }
  };

  const renderUserProfilePane = () => {
    if (!selectedPaneUser) {
      return (
        <View style={styles.profilePaneEmpty}>
          <Ionicons name="person-circle-outline" size={48} color={colors.text.secondary} />
          <Text style={styles.profilePaneEmptyTitle}>{t('userSetup.teamMembersCount', { count: users.length })}</Text>
          <Text style={styles.profilePaneEmptyText}>{t('permissions.targetPicker.selectTarget')}</Text>
        </View>
      );
    }

    const assignedDepartments = userDepartments[selectedPaneUser.userId] || [];

    return (
      <ScrollView style={styles.profilePaneScroll} contentContainerStyle={styles.profilePaneContent}>
        <View style={styles.profilePaneHeader}>
          <View style={styles.profilePaneAvatar}>
            <Text style={styles.profilePaneAvatarText}>
              {(selectedPaneUser.user?.firstName?.charAt(0) ?? '') || (selectedPaneUser.user?.email?.charAt(0) ?? '?')}
              {selectedPaneUser.user?.lastName?.charAt(0) ?? ''}
            </Text>
          </View>
          <Text style={styles.profilePaneName}>{getUserDisplayName(selectedPaneUser)}</Text>
          <Text style={styles.profilePaneEmail}>{selectedPaneUser.user?.email ?? ''}</Text>
        </View>

        <View style={styles.profilePaneSection}>
          <Text style={styles.profilePaneLabel}>{t('userSetup.role')}</Text>
          <TouchableOpacity
            style={[styles.profilePaneAction, { flexDirection: rtl.rowDirection }]}
            onPress={() => handleRoleChange(selectedPaneUser)}
            disabled={selectedPaneUser.role === 'owner'}
          >
            <View style={[styles.roleBadge, { flexDirection: rtl.rowDirection, backgroundColor: `${String(getRoleColor(selectedPaneUser.role))}20` }]}>
              <Ionicons name={selectedPaneUser.role === 'admin' ? 'shield' : selectedPaneUser.role === 'manager' ? 'briefcase' : selectedPaneUser.role === 'member' ? 'person' : 'eye'} size={12} color={getRoleColor(selectedPaneUser.role)} />
              <Text style={[styles.roleText, rtl.marginStart(spacing.xs), { color: getRoleColor(selectedPaneUser.role) }]}>
                {getRoleLabel(selectedPaneUser.role)}
              </Text>
            </View>
            {selectedPaneUser.role !== 'owner' && <DirectionalIcon name="chevron-forward" size={18} color={colors.text.secondary} />}
          </TouchableOpacity>
        </View>

        <View style={styles.profilePaneSection}>
          <Text style={styles.profilePaneLabel}>{t('settings.items.departments.title')}</Text>
          <View style={styles.departmentChipWrap}>
            {assignedDepartments.length > 0 ? assignedDepartments.map((department) => (
              <View key={department._id} style={styles.departmentChip}>
                <Text style={styles.departmentChipText}>{department.name}</Text>
              </View>
            )) : (
              <Text style={styles.profilePaneEmptyText}>{t('userSetup.noDepartmentsAssigned')}</Text>
            )}
          </View>
          {selectedPaneUser.role !== 'owner' && (
            <TouchableOpacity style={[styles.profilePaneButton, { flexDirection: rtl.rowDirection }]} onPress={() => {
              setSelectedIds(new Set([selectedPaneUser.userId]));
              setShowBulkDepartmentPicker(true);
            }}>
              <Ionicons name="business-outline" size={16} color={colors.primary} />
              <Text style={styles.profilePaneButtonText}>{t('userSetup.bulk.assignDepartment')}</Text>
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.profilePaneSection}>
          <PermissionSummaryChip
            targetType="user"
            targetId={selectedPaneUser.userId}
            summary={userSummaries[selectedPaneUser.userId]}
            onPress={() => navigateTo('business-permissions', undefined, { targetType: 'user', targetId: selectedPaneUser.userId })}
          />
        </View>

        {selectedPaneUser.role !== 'owner' && (
          <View style={styles.profilePaneSection}>
            {selectedPaneUser.userStatus === 'pending' && (
              <TouchableOpacity style={[styles.profilePaneButton, { flexDirection: rtl.rowDirection }]} onPress={() => handleResendInvitation(selectedPaneUser)}>
                <Ionicons name="mail-outline" size={16} color={colors.warning} />
                <Text style={[styles.profilePaneButtonText, { color: colors.warning }]}>{t('userSetup.resendInvitationConfirm')}</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity style={[styles.profilePaneButton, { flexDirection: rtl.rowDirection }]} onPress={() => handleRemoveUser(selectedPaneUser)}>
              <Ionicons name="trash-outline" size={16} color={colors.error} />
              <Text style={[styles.profilePaneButtonText, { color: colors.error }]}>{t('userSetup.removeUserTitle')}</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>
    );
  };

  // Render user card
  const renderUserCard = (businessUser: BusinessUser) => {
    
    const isSelected = selectedIds.has(businessUser.userId);
    return (
    <TouchableOpacity
      key={businessUser._id}
      style={[styles.userCard, { flexDirection: rtl.rowDirection }]}
      activeOpacity={selectionMode && businessUser.role !== 'owner' ? 0.7 : 1}
      onPress={() => {
        handleUserCardPress(businessUser);
      }}
    >
      {selectionMode && businessUser.role !== 'owner' && (
        <View style={styles.checkboxWrapper}>
          <View style={[styles.checkbox, isSelected && styles.checkboxChecked]}>
            {isSelected && <Ionicons name="checkmark" size={14} color={colors.white} />}
          </View>
        </View>
      )}
      <View style={[styles.userInfo, { flexDirection: rtl.rowDirection }]}>
        <View style={[styles.avatar, rtl.marginEnd(spacing.sm)]}>
          <Text style={styles.avatarText}>
            {(businessUser.user?.firstName?.charAt(0) ?? '') || (businessUser.user?.email?.charAt(0) ?? '?')}
            {businessUser.user?.lastName?.charAt(0) ?? ''}
          </Text>
        </View>
        <View style={styles.userDetails}>
          <View style={[styles.userNameContainer, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.userName, { textAlign: rtl.textStart }]}>
              {businessUser.user?.firstName || businessUser.user?.lastName
                ? `${businessUser.user?.firstName ?? ''} ${businessUser.user?.lastName ?? ''}`.trim()
                : (businessUser.user?.email ?? t('userSetup.pendingInvite'))}
            </Text>
            {businessUser.role === 'owner' && (
              <View style={[styles.ownerBadge, rtl.marginStart(spacing.sm)]}>
                <Ionicons name="star" size={12} color={colors.accent} />
                <Text style={[styles.ownerText, rtl.marginStart(2)]}>{t("userSetup.owner")}</Text>
              </View>
            )}
          </View>
          <Text style={[styles.userEmail, { textAlign: rtl.textStart }]}>{businessUser.user?.email ?? ''}</Text>
          {businessUser.user?.phone && (
            <Text style={[styles.userPhone, { textAlign: rtl.textStart }]}>{businessUser.user.phone}</Text>
          )}
          <View style={[styles.userMeta, { flexDirection: rtl.rowDirection }]}>
            <View style={[styles.roleBadge, { flexDirection: rtl.rowDirection, backgroundColor: `${String(getRoleColor(businessUser.role))}20` }]}>
              <Ionicons
                name={businessUser.role === 'admin' ? 'shield' :
                  businessUser.role === 'manager' ? 'briefcase' :
                    businessUser.role === 'member' ? 'person' : 'eye'}
                size={12}
                color={getRoleColor(businessUser.role)}
              />
              <Text style={[styles.roleText, rtl.marginStart(spacing.xs), { color: getRoleColor(businessUser.role) }]}>
                {getRoleLabel(businessUser.role)}
              </Text>
            </View>
            <View style={[styles.statusBadge, { flexDirection: rtl.rowDirection, backgroundColor: `${String(getStatusColor(businessUser.userStatus))}20` }]}>
              <View style={[styles.statusDot, rtl.marginEnd(spacing.xs), { backgroundColor: getStatusColor(businessUser.userStatus) }]} />
              <Text style={[styles.statusText, { color: getStatusColor(businessUser.userStatus) }]}>
                {getStatusLabel(businessUser.userStatus)}
              </Text>
            </View>
            <PermissionSummaryChip
              targetType="user"
              targetId={businessUser.userId}
              summary={userSummaries[businessUser.userId]}
              onPress={() => navigateTo('business-permissions', undefined, { targetType: 'user', targetId: businessUser.userId })}
            />
          </View>
          {(userDepartments[businessUser.userId] || []).length > 0 && (
            <View style={styles.cardDepartmentWrap}>
              {(userDepartments[businessUser.userId] || []).slice(0, 3).map((department) => (
                <View key={department._id} style={styles.cardDepartmentChip}>
                  <Text style={styles.cardDepartmentChipText}>{department.name}</Text>
                </View>
              ))}
              {(userDepartments[businessUser.userId] || []).length > 3 && (
                <Text style={styles.cardDepartmentMore}>+{(userDepartments[businessUser.userId] || []).length - 3}</Text>
              )}
            </View>
          )}
        </View>
      </View>

      {!selectionMode && (
        <View style={[styles.userActions, { flexDirection: rtl.rowDirection }]}>
          {businessUser.role !== 'owner' && (
            <>
              {/* Resend invitation button for pending users */}
              {businessUser.userStatus === 'pending' && (
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => handleResendInvitation(businessUser)}
                >
                  <Ionicons name="mail-outline" size={16} color={colors.warning} />
                </TouchableOpacity>
              )}

              {/* Change role button for active users */}
              {businessUser.userStatus === 'active' && (
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => handleRoleChange(businessUser)}
                >
                  <Ionicons name="person-outline" size={16} color={colors.primary} />
                </TouchableOpacity>
              )}

              {/* Remove user button */}
              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => handleRemoveUser(businessUser)}
              >
                <Ionicons name="trash-outline" size={16} color={colors.error} />
              </TouchableOpacity>
            </>
          )}
        </View>
      )}
    </TouchableOpacity>
    );
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    handleConfirmDeleteUser();
  };

  // Handle resend confirmation
  const handleResendConfirm = () => {
    handleConfirmResendInvitation();
  };

  // Render role change modal content
  const renderRoleModalContent = () => {
    console.log('🎨 [UserSetupScreen] Rendering role modal content for user:', selectedUser);
    console.log('🎨 [UserSetupScreen] Current user role:', selectedUser?.role);
    
    return (
      <>
        <Text style={styles.modalSubtitle}>
          {t('userSetup.selectNewRoleFor', { name: `${selectedUser?.user?.firstName ?? ''} ${selectedUser?.user?.lastName ?? ''}`.trim() })}
        </Text>

        <View style={styles.roleOptions}>
          {['admin', 'manager', 'member', 'viewer'].map((role) => {
            console.log('🎨 [UserSetupScreen] Rendering role option:', role, 'isSelected:', selectedUser?.role === role);
            return (
          <TouchableOpacity
            key={role}
            style={[
              styles.roleOptionItem,
              { flexDirection: rtl.rowDirection },
              selectedUser?.role === role && styles.roleOptionItemSelected
            ]}
            onPress={() => handleUpdateUserRole(selectedUser!.userId, role)}
          >
            <View style={[styles.roleOptionContent, { flexDirection: rtl.rowDirection }]}>
              <Ionicons
                name={role === 'admin' ? 'shield' :
                  role === 'manager' ? 'briefcase' :
                    role === 'member' ? 'person' : 'eye'}
                size={20}
                color={selectedUser?.role === role ? colors.white : getRoleColor(role)}
              />
              <Text style={[
                styles.roleOptionItemText,
                { color: selectedUser?.role === role ? colors.white : getRoleColor(role) }
              ]}>
                {getRoleLabel(role)}
              </Text>
            </View>
            {selectedUser?.role === role && (
              <Ionicons name="checkmark" size={20} color={colors.white} />
            )}
          </TouchableOpacity>
            );
          })}
        </View>
      </>
    );
  };

  // Render invite modal content
  const renderInviteModalContent = () => (
    <ScrollView 
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.scrollContentContainer}
    >
      <View style={styles.inputGroup}>
        <Text style={[styles.inputLabel, styles.inputLabelRequired, { textAlign: rtl.textStart }]}>
          {t('userSetup.emailAddress')} <Text style={styles.requiredAsterisk}>*</Text>
        </Text>
        <TextInput
          style={[
            styles.input,
            { textAlign: rtl.textStart },
            !inviteData.email?.trim() && styles.inputRequired
          ]}
          value={inviteData.email}
          onChangeText={(text) => setInviteData({ ...inviteData, email: text })}
          placeholder={t('userSetup.emailPlaceholder')}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          spellCheck={false}
          returnKeyType="next"
          blurOnSubmit={false}
        />
        {!inviteData.email?.trim() && (
          <Text style={[styles.requiredText, { textAlign: rtl.textStart }]}>{t('userSetup.emailRequired')}</Text>
        )}
      </View>

      <View style={[styles.inputRow, { flexDirection: rtl.rowDirection }]}>
        <View style={[styles.inputGroup, { flex: 1 }, rtl.marginEnd(spacing.sm)]}>
          <Text style={[styles.inputLabel, styles.inputLabelRequired, { textAlign: rtl.textStart }]}>
            {t('userSetup.firstName')} <Text style={styles.requiredAsterisk}>*</Text>
          </Text>
          <TextInput
            style={[
              styles.input,
              { textAlign: rtl.textStart },
              !inviteData.firstName?.trim() && styles.inputRequired
            ]}
            value={inviteData.firstName}
            onChangeText={(text) => setInviteData({ ...inviteData, firstName: text })}
            placeholder={t('userSetup.firstNamePlaceholder')}
            autoCapitalize="words"
          />
          {!inviteData.firstName?.trim() && (
            <Text style={[styles.requiredText, { textAlign: rtl.textStart }]}>{t('userSetup.firstNameRequired')}</Text>
          )}
        </View>
        <View style={[styles.inputGroup, { flex: 1 }]}>
          <Text style={[styles.inputLabel, styles.inputLabelRequired, { textAlign: rtl.textStart }]}>
            {t('userSetup.lastName')} <Text style={styles.requiredAsterisk}>*</Text>
          </Text>
          <TextInput
            style={[
              styles.input,
              { textAlign: rtl.textStart },
              !inviteData.lastName?.trim() && styles.inputRequired
            ]}
            value={inviteData.lastName}
            onChangeText={(text) => setInviteData({ ...inviteData, lastName: text })}
            placeholder={t('userSetup.lastNamePlaceholder')}
            autoCapitalize="words"
          />
          {!inviteData.lastName?.trim() && (
            <Text style={[styles.requiredText, { textAlign: rtl.textStart }]}>{t('userSetup.lastNameRequired')}</Text>
          )}
        </View>
      </View>

      <View style={styles.inputGroup}>
        <Text style={[styles.inputLabel, { textAlign: rtl.textStart }]}>{t('userSetup.departmentsOptional')}</Text>
        <TouchableOpacity style={styles.input} onPress={() => setShowInviteDepartmentPicker(true)}>
          <Text style={{ color: inviteDepartmentIds.length ? colors.text.primary : colors.text.secondary }}>
            {t('userSetup.selectDepartments')}
          </Text>
        </TouchableOpacity>
        {inviteDepartmentIds.length > 0 && (
          <View style={styles.cardDepartmentWrap}>
            {inviteDepartmentIds.map((id) => {
              const department = allDepartments.find(d => d._id === id);
              if (!department) return null;
              return (
                <View key={id} style={[styles.cardDepartmentChip, styles.removableChip]}>
                  <Text style={styles.cardDepartmentChipText}>{department.name}</Text>
                  <TouchableOpacity
                    onPress={() => setInviteDepartmentIds(prev => prev.filter(d => d !== id))}
                    hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                  >
                    <Ionicons name="close" size={12} color={colors.text.secondary} />
                  </TouchableOpacity>
                </View>
              );
            })}
          </View>
        )}
      </View>

      <View style={styles.inputGroup}>
        <Text style={[styles.inputLabel, { textAlign: rtl.textStart }]}>{t('userSetup.notesOptional')}</Text>
        <TextInput
          style={[styles.input, styles.textArea, { textAlign: rtl.textStart }]}
          value={inviteData.notes}
          onChangeText={(text) => setInviteData({ ...inviteData, notes: text })}
          placeholder={t('userSetup.notesPlaceholder')}
          multiline
          numberOfLines={3}
        />
      </View>

      <BusinessUserPicker
        visible={showInviteDepartmentPicker}
        onClose={() => setShowInviteDepartmentPicker(false)}
        onConfirm={(selectedIds) => {
          setInviteDepartmentIds(selectedIds);
          setShowInviteDepartmentPicker(false);
        }}
        mode="multiple"
        source="departments"
        selectedUserIds={inviteDepartmentIds}
        title={t('userSetup.selectDepartments')}
      />
    </ScrollView>
  );

  // Render invite modal footer
  const renderInviteModalFooter = () => (
    <View style={[styles.modalFooter, { flexDirection: rtl.rowDirection }]}>
      <TouchableOpacity
        style={styles.cancelButton}
        onPress={() => setShowInviteModal(false)}
      >
        <Text style={styles.cancelButtonText}>{t('userSetup.cancel')}</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[
          styles.inviteButton,
          { flexDirection: rtl.rowDirection },
          (inviting || !inviteData.email?.trim() || !inviteData.firstName?.trim() || !inviteData.lastName?.trim()) && styles.inviteButtonDisabled
        ]}
        onPress={handleInviteUser}
        disabled={inviting || !inviteData.email?.trim() || !inviteData.firstName?.trim() || !inviteData.lastName?.trim()}
      >
        <Text style={styles.inviteButtonText}>
          {inviting ? t('userSetup.sending') : t('userSetup.sendInvitation')}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const headerActions: FormHeaderAction[] = !currentBusiness ? [] : selectionMode
    ? [{ icon: 'close', label: t('userSetup.bulk.exitSelection'), onPress: exitUserSelectionMode, accessibilityLabel: t('userSetup.bulk.exitSelection') }]
    : [
      ...(users.length > 0 ? [{ icon: 'checkbox-outline' as const, onPress: () => setSelectionMode(true), accessibilityLabel: t('userSetup.bulk.selectAll') }] : []),
      { icon: 'business-outline', onPress: () => navigateTo('departments'), accessibilityLabel: t('settings.items.departments.title') },
      ...(userLimits?.canAddUser ? [{ icon: 'person-add' as const, label: t('userSetup.inviteUser'), primary: true, onPress: () => setShowInviteModal(true), accessibilityLabel: t('userSetup.inviteUser') }] : []),
    ];

  return (
    <>
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        activeSettingsItemId="team"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => setSelectedPaneUser(null)}
        rightPaneContent="custom"
        rightPaneCustomContent={renderUserProfilePane()}
        formHeader={
          <FormHeader
            title={t('settings.items.team.title')}
            subtitle={currentBusiness ? [currentBusiness.name, userLimits ? t('userSetup.usersOfMax', { current: userLimits.currentUsers, max: userLimits.maxUsers }) : ''].filter(Boolean).join(' · ') : undefined}
            onBack={goBack}
            actions={headerActions}
          />
        }
      >
      <View style={styles.screen}>
        {/* Toolbar: search + seat usage (title, count and actions live in the form header). */}
        <View style={styles.toolbar}>
            <View style={[
            styles.searchInputContainer,
            { flexDirection: rtl.rowDirection },
            !currentBusiness && styles.searchInputDisabled
          ]}>
            <Ionicons
              name="search"
              size={20}
              color={!currentBusiness ? colors.text.disabled : colors.text.secondary}
              style={rtl.marginEnd(spacing.sm)}
            />
            <TextInput
              style={[
                styles.searchInput,
                { textAlign: rtl.textStart },
                !currentBusiness && styles.searchInputTextDisabled
              ]}
              placeholder={
                !currentBusiness
                  ? t('userSetup.searchPlaceholderNoBusiness')
                  : t('userSetup.searchPlaceholder')
              }
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor={colors.text.secondary}
              editable={!!currentBusiness}
            />
            {searchQuery.length > 0 && currentBusiness && (
              <TouchableOpacity
                style={[styles.clearSearchButton, rtl.marginStart(spacing.sm)]}
                onPress={() => setSearchQuery('')}
              >
                <Ionicons name="close-circle" size={20} color={colors.text.secondary} />
              </TouchableOpacity>
            )}
          </View>
          {userLimits && (
            <View style={[styles.seatRow, { flexDirection: rtl.rowDirection }]}>
              <Text style={[styles.seatText, !userLimits.canAddUser && { color: colors.error }]} numberOfLines={1}>
                {t('userSetup.usersOfMax', { current: userLimits.currentUsers, max: userLimits.maxUsers })} · {t('userSetup.slotsRemaining', { count: userLimits.remainingSlots })}
              </Text>
              <View style={styles.seatBar}>
                <View style={[styles.seatFill, {
                  width: `${userLimits.maxUsers > 0 ? Math.min((userLimits.currentUsers / userLimits.maxUsers) * 100, 100) : 0}%`,
                  backgroundColor: userLimits.canAddUser ? colors.primary : colors.error,
                }]} />
              </View>
            </View>
          )}
        </View>

          {/* Bulk action bar */}
          {selectionMode && (
            <View style={[styles.bulkActionBar, styles.bulkActionBarDocked, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity style={styles.bulkActionButton} onPress={handleSelectAllUsers}>
                <Ionicons name="checkmark-done-outline" size={16} color={colors.primary} />
                <Text style={styles.bulkActionText}>{t('userSetup.bulk.selectAll')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.bulkActionButton}
                onPress={() => setShowBulkRoleModal(true)}
                disabled={selectedIds.size === 0}
              >
                <Ionicons name="shield-outline" size={16} color={selectedIds.size === 0 ? colors.text.disabled : colors.primary} />
                <Text style={[styles.bulkActionText, selectedIds.size === 0 && styles.bulkActionTextDisabled]}>{t('userSetup.bulk.changeRole')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.bulkActionButton}
                onPress={() => setShowBulkDepartmentPicker(true)}
                disabled={selectedIds.size === 0}
              >
                <Ionicons name="business-outline" size={16} color={selectedIds.size === 0 ? colors.text.disabled : colors.primary} />
                <Text style={[styles.bulkActionText, selectedIds.size === 0 && styles.bulkActionTextDisabled]}>{t('userSetup.bulk.assignDepartment')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.bulkActionButton}
                onPress={handleBulkSetPermissions}
                disabled={selectedIds.size === 0}
              >
                <Ionicons name="shield-checkmark-outline" size={16} color={selectedIds.size === 0 ? colors.text.disabled : colors.primary} />
                <Text style={[styles.bulkActionText, selectedIds.size === 0 && styles.bulkActionTextDisabled]}>{t('userSetup.bulk.setPermissions')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.bulkActionButton}
                onPress={() => setShowBulkDeleteModal(true)}
                disabled={selectedIds.size === 0}
              >
                <Ionicons name="trash-outline" size={16} color={selectedIds.size === 0 ? colors.text.disabled : colors.error} />
                <Text style={[styles.bulkActionText, { color: colors.error }, selectedIds.size === 0 && styles.bulkActionTextDisabled]}>{t('userSetup.bulk.remove')}</Text>
              </TouchableOpacity>
            </View>
          )}

        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          {!!currentBusiness && <AiAgentsSection />}
          {!!currentBusiness && !loading && users.length > 0 && (
            <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
              <View style={[styles.sectionTitleContainer, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="people-outline" size={20} color={colors.primary} />
                <Text style={[styles.sectionTitle, rtl.marginStart(spacing.sm)]}>
                  {searchQuery
                    ? t('userSetup.teamMembersFiltered', { filtered: filteredUsers.length, total: users.length })
                    : t('userSetup.teamMembersCount', { count: users.length })}
                </Text>
              </View>
            </View>
          )}
          {!currentBusiness ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="business-outline" size={48} color={colors.text.secondary} />
              <Text style={styles.emptyTitle}>{t('userSetup.noBusinessFound')}</Text>
              <Text style={styles.emptySubtitle}>
                {t('userSetup.noBusinessFoundDesc')}
              </Text>
              <TouchableOpacity
                style={styles.createBusinessButton}
                onPress={() => navigateTo('business-setup')}
              >
                <Text style={styles.createBusinessButtonText}>{t('userSetup.createBusiness')}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              {loading ? (
                <View style={styles.loadingContainer}>
                  <Text style={styles.loadingText}>{t('userSetup.loadingUsers')}</Text>
                </View>
              ) : users.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Ionicons name="people-outline" size={48} color={colors.text.secondary} />
                  <Text style={styles.emptyTitle}>{t('userSetup.noUsersFound')}</Text>
                  <Text style={styles.emptySubtitle}>
                    {userLimits?.canAddUser
                      ? t('userSetup.inviteFirstUser')
                      : t('userSetup.userLimitReached')
                    }
                  </Text>
                </View>
              ) : filteredUsers.length === 0 && searchQuery ? (
                <View style={styles.emptyContainer}>
                  <Ionicons name="search-outline" size={48} color={colors.text.secondary} />
                  <Text style={styles.emptyTitle}>{t('userSetup.noUsersFound')}</Text>
                  <Text style={styles.emptySubtitle}>
                    {t('userSetup.noUsersMatchSearch', { query: searchQuery })}
                  </Text>
                  <TouchableOpacity
                    style={styles.clearSearchButton}
                    onPress={() => setSearchQuery('')}
                  >
                    <Text style={styles.clearSearchText}>{t('userSetup.clearSearch')}</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <View style={styles.usersList}>
                  {filteredUsers.map(renderUserCard)}
                </View>
              )}
            </>
          )}
        </ScrollView>
      </View>


      {/* Invite Modal */}
      <Modal
        visible={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title={t('userSetup.inviteModalTitle')}
        size="large"
        type="form"
        scrollable={true}
        footer={renderInviteModalFooter()}
      >
        {renderInviteModalContent()}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('userSetup.removeUserTitle')}
        message={selectedUser ? t('userSetup.removeUserMessage', { name: `${selectedUser.user?.firstName ?? ''} ${selectedUser.user?.lastName ?? ''}`.trim() || (selectedUser.user?.email ?? '') }) : ''}
        confirmText={t('userSetup.removeUserTitle')}
        cancelText={t('userSetup.cancel')}
        type="warning"
      />

      {/* Resend Confirmation Modal */}
      <ConfirmationModal
        visible={showResendModal}
        onClose={() => {
          setShowResendModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleResendConfirm}
        title={t('userSetup.resendInvitationTitle')}
        message={selectedUser ? t('userSetup.resendInvitationMessage', { name: `${selectedUser.user?.firstName ?? ''} ${selectedUser.user?.lastName ?? ''}`.trim() || (selectedUser.user?.email ?? '') }) : ''}
        confirmText={t('userSetup.resendInvitationConfirm')}
        cancelText={t('userSetup.cancel')}
        type="info"
      />

      {/* Role Change Modal */}
      <Modal
        visible={showRoleModal}
        onClose={() => {
          setShowRoleModal(false);
          setSelectedUser(null);
        }}
        title={t('userSetup.changeRoleTitle')}
        size="small"
        type="form"
      >
        {renderRoleModalContent()}
      </Modal>

      {/* Bulk Role Change Modal */}
      <Modal
        visible={showBulkRoleModal}
        onClose={() => setShowBulkRoleModal(false)}
        title={t('userSetup.bulk.changeRole')}
        size="small"
        type="form"
      >
        <Text style={styles.modalSubtitle}>
          {t('userSetup.bulk.selectedCount', { count: selectedIds.size })}
        </Text>
        <View style={styles.roleOptions}>
          {(['admin', 'manager', 'member', 'viewer'] as const).map((role) => (
            <TouchableOpacity
              key={role}
              style={[styles.roleOptionItem, { flexDirection: rtl.rowDirection }]}
              onPress={() => handleBulkRoleConfirm(role)}
              disabled={bulkRoleSaving}
            >
              <View style={[styles.roleOptionContent, { flexDirection: rtl.rowDirection }]}>
                <Ionicons
                  name={role === 'admin' ? 'shield' : role === 'manager' ? 'briefcase' : role === 'member' ? 'person' : 'eye'}
                  size={20}
                  color={getRoleColor(role)}
                />
                <Text style={[styles.roleOptionItemText, { color: getRoleColor(role) }]}>
                  {getRoleLabel(role)}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </Modal>

      {/* Bulk Remove Confirmation */}
      <ConfirmationModal
        visible={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        onConfirm={handleBulkRemoveConfirm}
        title={t('userSetup.bulk.confirmRemoveTitle')}
        message={t('userSetup.bulk.confirmRemoveMessage', { count: selectedIds.size })}
        confirmText={t('userSetup.bulk.remove')}
        cancelText={t('userSetup.cancel')}
        loading={bulkDeleting}
        type="error"
      />

      {/* Bulk Assign Department Picker */}
      <BusinessUserPicker
        visible={showBulkDepartmentPicker}
        onClose={() => setShowBulkDepartmentPicker(false)}
        onConfirm={handleBulkDepartmentConfirm}
        mode="single"
        source="departments"
        title={t('userSetup.bulk.assignDepartment')}
      />
      </ScreenLayout>
      <MoreOptionsModal
        visible={showMoreModal}
        onClose={() => setShowMoreModal(false)}
      />
    </>
  );
};

const getStyles = () => StyleSheet.create({
  sectionHeaderActions: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textButton: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  textButtonLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily?.semibold,
    color: colors.primary,
  },
  bulkActionBar: {
    backgroundColor: colors.background.secondary,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 8,
    marginBottom: spacing.md,
    gap: spacing.lg,
    flexWrap: 'wrap',
  },
  bulkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  bulkActionText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily?.medium,
    color: colors.primary,
  },
  bulkActionTextDisabled: {
    color: colors.text.disabled,
  },
  checkboxWrapper: {
    paddingRight: spacing.sm,
    justifyContent: 'center',
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  screen: { flex: 1, backgroundColor: colors.lightGray },
  toolbar: {
    backgroundColor: colors.background.surface,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    gap: spacing.xs,
    ...designTokens.shadows.sm,
  },
  seatRow: { alignItems: 'center', gap: spacing.sm },
  seatText: { flexShrink: 1, fontSize: typography.fontSize.xs, color: colors.text.secondary },
  seatBar: { flex: 1, minWidth: 60, height: 4, borderRadius: 2, backgroundColor: colors.border, overflow: 'hidden' },
  seatFill: { height: '100%', borderRadius: 2 },
  bulkActionBarDocked: { marginHorizontal: spacing.md, marginTop: spacing.sm, marginBottom: 0 },
  content: {
    flex: 1,
    width: Platform.OS === 'web' && !isMobileBrowser ? '100%' : undefined,
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg,
  },
  headerSection: {
    marginBottom: Platform.OS === 'android' ? 2 : spacing.lg, // More aggressive reduction
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.secondary,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  refreshButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    marginLeft: spacing.xs,
    fontWeight: '500',
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: 'bold',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  subtitle: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    lineHeight: 20,
  },
  businessInfo: {
    alignItems: 'center',
    backgroundColor: colors.background.secondary,
    padding: spacing.sm,
    borderRadius: 8,
  },
  businessIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  businessIconText: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    color: colors.white,
  },
  businessDetails: {
    flex: 1,
  },
  businessName: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  businessEmail: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  limitsCard: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.lg,
    marginBottom: Platform.OS === 'android' ? spacing.xs : spacing.lg, // More aggressive reduction
    shadowColor: colors.text.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  limitsHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  limitsTitleContainer: {
    alignItems: 'center',
  },
  limitsTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
  },
  limitBadge: {
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 4,
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border,
  },
  limitText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.text.primary,
  },
  limitsContent: {
    justifyContent: 'space-around',
    marginBottom: spacing.sm,
  },
  limitItem: {
    alignItems: 'center',
    flex: 1,
  },
  limitItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  limitLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
  limitValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
    color: colors.success,
  },
  progressContainer: {
    marginTop: spacing.sm,
  },
  progressBar: {
    height: 8,
    backgroundColor: colors.background.secondary,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: spacing.xs,
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  progressText: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    textAlign: 'center',
  },
  usersSection: {
    marginBottom: Platform.OS === 'android' ? spacing.sm : spacing.xl, // More aggressive reduction
  },
  sectionHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  sectionTitleContainer: {
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
  },
  inviteButton: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: 8,
  },
  inviteButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
  },
  loadingText: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: spacing.lg,
  },
  createBusinessButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 8,
  },
  createBusinessButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontWeight: '600',
  },
  usersList: {
    gap: spacing.sm,
  },
  userCard: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    shadowColor: colors.text.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  avatarText: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    color: colors.white,
  },
  userDetails: {
    flex: 1,
  },
  userNameContainer: {
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  userName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.text.primary,
    flex: 1,
  },
  ownerBadge: {
    alignItems: 'center',
    backgroundColor: colors.accent,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: 4,
  },
  ownerText: {
    fontSize: typography.fontSize.xs,
    fontWeight: '600',
    color: colors.white,
  },
  userEmail: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginBottom: spacing.sm,
  },
  userMeta: {
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  cardDepartmentWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  cardDepartmentChip: {
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
  },
  removableChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  cardDepartmentChipText: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily?.medium,
  },
  cardDepartmentMore: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    paddingVertical: spacing.xs / 2,
  },
  roleBadge: {
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 4,
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border,
  },
  roleText: {
    fontSize: typography.fontSize.xs,
    fontWeight: '600',
  },
  statusBadge: {
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 4,
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: typography.fontSize.xs,
    fontWeight: '600',
  },
  userActions: {
    gap: spacing.sm,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.secondary,
  },
  // Modal content styles (for form elements)
  inputGroup: {
    marginBottom: spacing.sm,
  },
  inputRow: {
    flexDirection: 'row',
    marginBottom: spacing.sm,
  },
  inputLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  inputLabelRequired: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
  },
  inputRequired: {
    borderColor: colors.error,
    borderWidth: 2,
    backgroundColor: `${String(colors.error)}14`,
  },
  requiredText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    marginTop: spacing.xs,
    fontWeight: '500',
  },
  requiredAsterisk: {
    color: colors.error,
    fontWeight: 'bold',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  scrollContentContainer: {
    paddingBottom: spacing.lg,
  },
  roleSelector: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  roleOption: {
    flex: 1,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
  },
  roleOptionSelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  roleOptionText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },
  roleOptionTextSelected: {
    color: colors.white,
    fontWeight: '600',
  },
  modalFooter: {
    flexDirection: 'row',
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: spacing.sm,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cancelButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
  },
  inviteButtonDisabled: {
    opacity: 0.6,
  },
  // Role modal styles
  roleModalBody: {
    padding: spacing.lg,
  },
  modalSubtitle: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    marginBottom: spacing.lg,
    textAlign: 'center',
  },
  roleOptions: {
    gap: spacing.sm,
  },
  roleOptionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.surface,
  },
  roleOptionItemSelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  roleOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  roleOptionItemText: {
    fontSize: typography.fontSize.base,
    fontWeight: '500',
    color: colors.text.primary,
  },
  // Search styles
  searchContainer: {
    marginBottom: Platform.OS === 'android' ? spacing.sm : spacing.lg,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.primary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  searchInputDisabled: {
    backgroundColor: colors.background.primary,
    borderColor: colors.border,
    opacity: 0.6,
  },
  searchIcon: {
    marginRight: spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    paddingVertical: 0,
  },
  searchInputTextDisabled: {
    color: colors.text.disabled,
  },
  clearSearchButton: {
    padding: spacing.xs,
  },
  clearSearchText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: '500',
  },
  // Phone number styles
  userPhone: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginTop: spacing.xs,
  },
  profilePaneScroll: {
    flex: 1,
  },
  profilePaneContent: {
    padding: spacing.lg,
    gap: spacing.lg,
  },
  profilePaneEmpty: {
    flex: 1,
    minHeight: 360,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
  },
  profilePaneEmptyTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily?.semibold,
    color: colors.text.primary,
    marginTop: spacing.md,
    textAlign: 'center',
  },
  profilePaneEmptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  profilePaneHeader: {
    alignItems: 'center',
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  profilePaneAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  profilePaneAvatarText: {
    fontSize: typography.fontSize.xl,
    color: colors.white,
    fontFamily: typography.fontFamily?.bold,
  },
  profilePaneName: {
    fontSize: typography.fontSize.lg,
    color: colors.text.primary,
    fontFamily: typography.fontFamily?.semibold,
    textAlign: 'center',
  },
  profilePaneEmail: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  profilePaneSection: {
    gap: spacing.sm,
  },
  profilePaneLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily?.medium,
  },
  profilePaneAction: {
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: spacing.md,
    backgroundColor: colors.background.surface,
  },
  departmentChipWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  departmentChip: {
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  departmentChipText: {
    fontSize: typography.fontSize.xs,
    color: colors.text.primary,
    fontFamily: typography.fontFamily?.medium,
  },
  profilePaneButton: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: spacing.md,
    backgroundColor: colors.background.surface,
  },
  profilePaneButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily?.semibold,
  },
});
